import type {
  BusinessHours,
  BusinessHoursDay,
  BusinessHoursDayKey,
  BusinessHoursWeekly,
  ServiceAvailabilityOverride,
  ServiceAvailabilityOverrideType,
} from '../types/firestore';

export const BUSINESS_HOURS_TIME_ZONE = 'Europe/Warsaw' as const;

export const BUSINESS_HOURS_DAY_ORDER: BusinessHoursDayKey[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];

export const BUSINESS_HOURS_DAY_LABELS: Record<BusinessHoursDayKey, string> = {
  mon: 'Poniedziałek',
  tue: 'Wtorek',
  wed: 'Środa',
  thu: 'Czwartek',
  fri: 'Piątek',
  sat: 'Sobota',
  sun: 'Niedziela',
};

const DEFAULT_DAY = (): BusinessHoursDay => ({
  enabled: false,
  open: null,
  close: null,
});

export const createEmptyBusinessHoursWeekly = (): BusinessHoursWeekly => ({
  mon: DEFAULT_DAY(),
  tue: DEFAULT_DAY(),
  wed: DEFAULT_DAY(),
  thu: DEFAULT_DAY(),
  fri: DEFAULT_DAY(),
  sat: DEFAULT_DAY(),
  sun: DEFAULT_DAY(),
});

export const createDefaultBusinessHours = (): BusinessHours => ({
  timezone: BUSINESS_HOURS_TIME_ZONE,
  weekly: createEmptyBusinessHoursWeekly(),
  updatedAt: null,
});

const TIME_VALUE_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const isValidTimeValue = (value: unknown): value is string =>
  typeof value === 'string' && TIME_VALUE_PATTERN.test(value.trim());

const normalizeTimeValue = (value: unknown): string | null =>
  isValidTimeValue(value) ? value.trim() : null;

const timeToMinutes = (value: string): number => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

export const normalizeBusinessHoursDay = (value: unknown): BusinessHoursDay => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const enabled = source.enabled === true;
  const open = normalizeTimeValue(source.open);
  const close = normalizeTimeValue(source.close);

  if (!enabled) {
    return { enabled: false, open: null, close: null };
  }

  return {
    enabled: true,
    open,
    close,
  };
};

export const normalizeBusinessHours = (value: unknown): BusinessHours | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const weeklySource =
    source.weekly && typeof source.weekly === 'object'
      ? (source.weekly as Record<string, unknown>)
      : null;

  if (!weeklySource) return null;

  const normalizedWeekly = BUSINESS_HOURS_DAY_ORDER.reduce((acc, key) => {
    acc[key] = normalizeBusinessHoursDay(weeklySource[key]);
    return acc;
  }, {} as BusinessHoursWeekly);

  return {
    timezone: BUSINESS_HOURS_TIME_ZONE,
    weekly: normalizedWeekly,
    updatedAt: (source.updatedAt as BusinessHours['updatedAt']) ?? null,
  };
};

export const validateBusinessHours = (value: unknown): string | null => {
  const normalized = normalizeBusinessHours(value);
  if (!normalized) {
    return 'Brak pełnej struktury godzin pracy.';
  }

  for (const dayKey of BUSINESS_HOURS_DAY_ORDER) {
    const day = normalized.weekly[dayKey];
    if (!day.enabled) continue;
    if (!day.open || !day.close) {
      return `Uzupełnij godziny dla dnia ${BUSINESS_HOURS_DAY_LABELS[dayKey]}.`;
    }
    if (timeToMinutes(day.open) >= timeToMinutes(day.close)) {
      return `Godzina otwarcia musi być wcześniejsza niż zamknięcia (${BUSINESS_HOURS_DAY_LABELS[dayKey]}).`;
    }
  }

  return null;
};

const getWarsawDateParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_HOURS_TIME_ZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const weekdayPart = parts.find((part) => part.type === 'weekday')?.value || 'Mon';
  const hour = Number(parts.find((part) => part.type === 'hour')?.value || '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value || '0');

  const weekdayMap: Record<string, BusinessHoursDayKey> = {
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
    Sun: 'sun',
  };

  return {
    dayKey: weekdayMap[weekdayPart] || 'mon',
    minutesNow: hour * 60 + minute,
  };
};

type NextOpeningResult = {
  dayKey: BusinessHoursDayKey;
  label: string;
  open: string;
};

const getNextOpening = (
  weekly: BusinessHoursWeekly,
  todayKey: BusinessHoursDayKey,
  minutesNow: number
): NextOpeningResult | null => {
  const startIndex = BUSINESS_HOURS_DAY_ORDER.indexOf(todayKey);
  for (let offset = 0; offset < BUSINESS_HOURS_DAY_ORDER.length; offset += 1) {
    const dayIndex = (startIndex + offset) % BUSINESS_HOURS_DAY_ORDER.length;
    const dayKey = BUSINESS_HOURS_DAY_ORDER[dayIndex];
    const day = weekly[dayKey];
    if (!day.enabled || !day.open) continue;
    if (offset === 0 && timeToMinutes(day.open) <= minutesNow) continue;

    return {
      dayKey,
      label: offset === 0 ? 'Dziś' : offset === 1 ? 'Jutro' : BUSINESS_HOURS_DAY_LABELS[dayKey],
      open: day.open,
    };
  }

  return null;
};

export type BusinessHoursLiveState = 'open' | 'closed' | 'unknown' | 'temporarily_unavailable';
export type ServiceAvailabilityStatusSource =
  | 'businessHours'
  | 'availabilityOverride'
  | 'legacyMissing';

export interface BusinessHoursLiveStatus {
  state: BusinessHoursLiveState;
  label: string;
  shortLabel?: string;
  helperText: string | null;
  description?: string;
  source?: ServiceAvailabilityStatusSource;
  isOpenForDisplay?: boolean;
  activeOverride?: {
    type: ServiceAvailabilityOverrideType;
    endsAt?: Date | null;
  };
}

export interface BusinessHoursLiveSummary extends BusinessHoursLiveStatus {
  summaryText: string;
}

export interface BusinessHoursDisplayRow {
  key: BusinessHoursDayKey;
  label: string;
  value: string;
  isToday: boolean;
}

export interface ServiceAvailabilityProviderReadModel {
  businessHours?: BusinessHours | null;
  availabilityOverride?: ServiceAvailabilityOverride | null;
}

export type ServiceAvailabilityStatus = Required<
  Pick<BusinessHoursLiveStatus, 'state' | 'label' | 'shortLabel' | 'source' | 'isOpenForDisplay'>
> &
  Pick<BusinessHoursLiveStatus, 'helperText' | 'description' | 'activeOverride'> & {
    summaryText: string;
  };

export const SERVICE_AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH = 160;

export const SERVICE_AVAILABILITY_OVERRIDE_LABELS: Record<ServiceAvailabilityOverrideType, string> =
  {
    vacation: 'Urlop',
    technical_break: 'Przerwa techniczna',
  };

const isAvailabilityOverrideType = (value: unknown): value is ServiceAvailabilityOverrideType =>
  value === 'vacation' || value === 'technical_break';

const toDateOrNull = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object') {
    const candidate = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof candidate.toDate === 'function') {
      const parsed = candidate.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }
    const seconds =
      typeof candidate.seconds === 'number'
        ? candidate.seconds
        : typeof candidate._seconds === 'number'
          ? candidate._seconds
          : null;
    if (seconds !== null) {
      const parsed = new Date(seconds * 1000);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  return null;
};

const normalizeAvailabilityOverrideMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed) return undefined;
  return trimmed.slice(0, SERVICE_AVAILABILITY_OVERRIDE_MESSAGE_MAX_LENGTH);
};

const formatOverrideEndDate = (date: Date): string =>
  new Intl.DateTimeFormat('pl-PL', {
    timeZone: BUSINESS_HOURS_TIME_ZONE,
    day: 'numeric',
    month: 'long',
  }).format(date);

export const getActiveAvailabilityOverride = (
  value: unknown,
  now: Date = new Date()
):
  | (NonNullable<ServiceAvailabilityStatus['activeOverride']> & {
      message?: string;
    })
  | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  if (source.enabled !== true || !isAvailabilityOverrideType(source.type)) return null;

  const startsAt = toDateOrNull(source.startsAt);
  const endsAt = toDateOrNull(source.endsAt);
  if (startsAt && startsAt.getTime() > now.getTime()) return null;
  if (endsAt && endsAt.getTime() <= now.getTime()) return null;

  return {
    type: source.type,
    endsAt,
    message: normalizeAvailabilityOverrideMessage(source.message),
  };
};

export const getBusinessHoursLiveStatus = (
  value: unknown,
  now: Date = new Date()
): BusinessHoursLiveStatus => {
  const normalized = normalizeBusinessHours(value);
  if (!normalized) {
    return {
      state: 'unknown',
      label: 'Godziny niepodane',
      shortLabel: 'Godziny niepodane',
      helperText: null,
      source: 'legacyMissing',
      isOpenForDisplay: false,
    };
  }

  const { dayKey, minutesNow } = getWarsawDateParts(now);
  const today = normalized.weekly[dayKey];

  if (today.enabled && today.open && today.close) {
    const openMinutes = timeToMinutes(today.open);
    const closeMinutes = timeToMinutes(today.close);

    if (minutesNow >= openMinutes && minutesNow < closeMinutes) {
      return {
        state: 'open',
        label: 'Czynny teraz',
        shortLabel: 'Czynny teraz',
        helperText: `Dziś do ${today.close}`,
        source: 'businessHours',
        isOpenForDisplay: true,
      };
    }
  }

  const nextOpening = getNextOpening(normalized.weekly, dayKey, minutesNow);
  return {
    state: 'closed',
    label: 'Nieczynny',
    shortLabel: 'Nieczynny',
    helperText: nextOpening ? `${nextOpening.label} od ${nextOpening.open}` : null,
    source: 'businessHours',
    isOpenForDisplay: false,
  };
};

const formatCompactHelperText = (status: BusinessHoursLiveStatus): string | null => {
  if (!status.helperText) {
    return null;
  }

  if (status.state === 'closed') {
    const match = status.helperText.match(/^(.+?) od (\d{2}:\d{2})$/u);
    if (match) {
      const [, dayLabel, time] = match;
      return `Otwiera ${dayLabel.toLowerCase()} o ${time}`;
    }
  }

  return status.helperText;
};

export const getBusinessHoursLiveSummary = (
  value: unknown,
  now: Date = new Date()
): BusinessHoursLiveSummary => {
  const status = getBusinessHoursLiveStatus(value, now);
  const compactHelperText = formatCompactHelperText(status);

  return {
    ...status,
    summaryText: compactHelperText ? `${status.label} • ${compactHelperText}` : status.label,
  };
};

export const resolveServiceAvailabilityStatus = (
  provider: ServiceAvailabilityProviderReadModel | null | undefined,
  now: Date = new Date()
): ServiceAvailabilityStatus => {
  const activeOverride = getActiveAvailabilityOverride(provider?.availabilityOverride, now);
  if (activeOverride) {
    const label = SERVICE_AVAILABILITY_OVERRIDE_LABELS[activeOverride.type];
    const endLabel = activeOverride.endsAt
      ? `do ${formatOverrideEndDate(activeOverride.endsAt)}`
      : null;
    const description =
      activeOverride.message ||
      'Serwis oznaczył tymczasową niedostępność. Odpowiedź może potrwać dłużej.';
    return {
      state: 'temporarily_unavailable',
      source: 'availabilityOverride',
      label,
      shortLabel: label,
      helperText: endLabel,
      description,
      summaryText: endLabel ? `${label} • ${endLabel}` : label,
      isOpenForDisplay: false,
      activeOverride: {
        type: activeOverride.type,
        endsAt: activeOverride.endsAt,
      },
    };
  }

  const businessHoursStatus = getBusinessHoursLiveSummary(provider?.businessHours, now);
  return {
    state: businessHoursStatus.state,
    source: businessHoursStatus.source || 'businessHours',
    label: businessHoursStatus.label,
    shortLabel: businessHoursStatus.shortLabel || businessHoursStatus.label,
    helperText: businessHoursStatus.helperText,
    description: businessHoursStatus.description,
    summaryText: businessHoursStatus.summaryText,
    isOpenForDisplay: businessHoursStatus.isOpenForDisplay === true,
    activeOverride: undefined,
  };
};

export const getBusinessHoursDisplayRows = (
  value: unknown,
  now: Date = new Date()
): BusinessHoursDisplayRow[] => {
  const normalized = normalizeBusinessHours(value);
  const todayKey = getWarsawDateParts(now).dayKey;
  const weekly = normalized?.weekly || createEmptyBusinessHoursWeekly();

  return BUSINESS_HOURS_DAY_ORDER.map((dayKey) => {
    const day = weekly[dayKey];
    const valueLabel =
      day.enabled && day.open && day.close ? `${day.open}–${day.close}` : 'Nieczynne';
    return {
      key: dayKey,
      label: BUSINESS_HOURS_DAY_LABELS[dayKey],
      value: valueLabel,
      isToday: dayKey === todayKey,
    };
  });
};
